import React, { Fragment, useCallback, useEffect, useState } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import some from 'lodash/some';
import { useSelector } from 'react-redux';
import noop from 'lodash/noop';

import { Button, CloseButtonTypes } from 'src/presentation/components/agent_desktop_components';
import NewThreadModal, { NewThreadModalRef } from './components/NewThreadModal';
import { selectCustomerId } from './actions/threadsUiSlice';
import sidekickAdminsResource from 'src/actions/sidekick/sidekickAdminsResource';
import sidekickCustomerResource from 'src/actions/sidekick/sidekickCustomerResource';
import SidekickThread, { SidekickThreadFast } from 'src/models/Sidekick/SidekickThreads';
import SidekickThreadEditor from './SidekickThreadEditor';
import SidekickThreadsActions from './SidekickThreadsActions';
import TablePage from 'src/presentation/components/pages/shared/TablePage';
import useCreateNewThread from 'src/presentation/components/pages/sidekick/threads/hooks/useCreateNewThread';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useResource, {
  isLoadingStatus,
  STATUS_FAILED,
  STATUS_SUCCEEDED,
  STATUS_UNSTARTED,
} from 'src/presentation/hooks/useResource';
import useRouter from 'src/presentation/hooks/useRouter';
import useHandleActivate from 'src/presentation/components/pages/sidekick/threads/hooks/useHandleActivate';
import { ModalWrapperRef } from './components/ModalWrapper';
import sidekickFastThreadsResource from 'src/actions/sidekick/sidekickFastThreadsResource';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import { HasActiveChildrenModal } from './components/HasActiveChildrenModal';
import { LoadingModal } from './components/LoadingModal';

const pageDataAid = 'admin-threads-page';

const filterFunc = (items, searchString) => {
  const searchPaths = ['name', 'channel'];
  if (searchString) {
    const search = searchString.toLowerCase().trim();
    return filter(items, item =>
      some(searchPaths, columnKey => get(item, columnKey, '').toLowerCase().includes(search))
    );
  }
  return items;
};

const tabConfig = [
  { label: 'Active', filter: (thread: SidekickThread) => thread.enabled, key: 'sidekick-treads-tab-enabled' },
  { label: 'Inactive', filter: thread => !thread.enabled, key: 'sidekick-treads-tab-disabled' },
];

const columnsConfig = [
  {
    attr: 'name',
    label: 'Name',
    sorting: true,
    customStyle: () => ({
      position: 'relative',
      width: '50%',
    }),
  },
  {
    attr: 'updatedAtString',
    label: 'Last Edited On',
    sorting: true,
  },
];

const SidekickThreads = ({ root }): React.ReactElement => {
  const customerId = useSelector(selectCustomerId);
  const {
    values: items,
    loadingState,
    errors,
  } = useResource(sidekickFastThreadsResource, { fetch: true, params: { customerId } });
  const [threads, setThreads] = useState<SidekickThreadFast[]>([]);
  const [threadsLoadingState, setThreadsLoadingState] = useState<string>(STATUS_UNSTARTED);

  useEffect(() => {
    if (loadingState === STATUS_FAILED) {
      setThreadsLoadingState(loadingState);
      return;
    }

    if (loadingState !== STATUS_SUCCEEDED) {
      return;
    }

    // Filter out legacy "Before & After" threads (those with next=false)
    // These threads should be edited in the legacy flow editor, not here
    const threads = items.reduce((acc: SidekickThreadFast[], thread) => {
      const name = thread?.name?.trim().toLowerCase();
      const isLegacyBeforeAfter = !thread.next && (name === 'before' || name === 'after');
      if (!isLegacyBeforeAfter) {
        acc.push({ ...thread, updatedAtString: toDateTimeString(thread.updatedAt) });
      }
      return acc;
    }, []);

    setThreadsLoadingState(loadingState);
    setThreads(threads);
  }, [items, loadingState, setThreads, setThreadsLoadingState]);

  const { history, location } = useRouter();

  const onNewThreadCreated = useCallback(
    lastCreatedThreadId => {
      history.push({ ...location, pathname: `${root}/${lastCreatedThreadId}` });
    },
    [history, root, location]
  );

  const newThreadModalRef = React.useRef<NewThreadModalRef>(null);
  const { show: showCreateNewThreadModal } = useCreateNewThread({ newThreadModalRef, onNewThreadCreated });

  const modalRef = React.useRef<ModalWrapperRef>(null);
  const [thread, setThread] = useState<SidekickThread | undefined>(undefined);
  const { toggleEnabledHandler } = useHandleActivate({ modalRef, setThread });

  const { loadingState: customerLoading } = useDocumentResource(sidekickCustomerResource, {
    fetch: true,
    params: { customerId },
  });

  const { loadingState: adminsLoadingState } = useResource(sidekickAdminsResource, {
    fetch: true,
    params: { customerId },
    body: { idDesc: true, limit: 100, customerId },
    headers: { 'X-Method': 'GET' },
  });

  const actionsRender = (
    <Button onClick={() => showCreateNewThreadModal()} data-aid="addNew-button">
      Create Thread
    </Button>
  );

  const renderRowActions = ({ item }) => {
    return (
      <>
        <SidekickThreadsActions
          hasActions={item.hasActions}
          item={item}
          newThreadModalRef={newThreadModalRef}
          root={root}
          toggleEnabledHandler={toggleEnabledHandler}
        />
      </>
    );
  };

  const isLoading =
    isLoadingStatus(threadsLoadingState) || isLoadingStatus(customerLoading) || isLoadingStatus(adminsLoadingState);

  return (
    <>
      <TablePage
        actions={actionsRender}
        renderRowActions={renderRowActions}
        columnConfig={columnsConfig}
        data-aid={pageDataAid}
        description={
          <>
            Create and sequence the Thread Actions and Sub-Actions for self-service conversations. Learn more about
            building Threads in our Help Docs.
          </>
        }
        editor={SidekickThreadEditor}
        emptyStateFullHeightLayout
        errors={errors}
        filterFunc={filterFunc}
        isLoading={isLoading}
        items={threads}
        modalCloseButtonType={CloseButtonTypes.NONE}
        modalTopPadding={0}
        name="Threads"
        resetErrors={noop}
        root={root}
        initialSorting={{ attr: 'name' }}
        tabConfig={tabConfig}
      />
      <NewThreadModal ref={newThreadModalRef} />

      <HasActiveChildrenModal root={root} modalRef={modalRef} thread={thread} />
      <LoadingModal />
    </>
  );
};

export default SidekickThreads;
